import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Upload, Download, CheckCircle, XCircle, Save, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { TeamLogo } from '@/lib/teamLogos';
import api from '@/lib/api';

export default function ScheduleImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');

  useEffect(() => {
    api.get('/api/seasons').then(r => {
      const active = r.data.filter(s => s.status === 'active');
      setSeasons(active);
      if (active.length > 0) setSelectedSeason(active[0].name);
    }).catch(() => {});
  }, []);

  const handleFile = useCallback((f) => {
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      setFile(f);
      setPreview(null);
    } else {
      toast.error('Please upload a CSV or Excel file');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const handleDragLeave = useCallback(() => setDragging(false), []);

  const handleUpload = async () => {
    if (!file) { toast.error(t('select_file')); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/matches/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      toast.success(`Parsed ${res.data.total_rows} rows`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!preview) return;
    if (!selectedSeason) { toast.error('Please select a season first'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/matches/import/save', { rows: preview.rows, file_name: preview.file_name, season: selectedSeason });
      toast.success(t('imported_matches', { count: res.data.imported }));
      navigate('/matches');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/api/matches/template/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'match_import_template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  return (
    <div data-testid="schedule-import-page" className="space-y-6 animate-fade-in max-w-5xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/matches')} className="text-base"><ArrowLeft size={16} className="mr-1" /> {t('back')}</Button>
        <h1 className="text-xl font-semibold text-gray-900">{t('import_season')}</h1>
      </div>

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardHeader><CardTitle className="text-sm font-medium text-gray-700">Select Season</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">Choose which season these matches will be imported into.</p>
          <Select value={selectedSeason} onValueChange={setSelectedSeason}>
            <SelectTrigger data-testid="import-season-select" className="w-60 bg-gray-50 border-gray-300 h-10 text-sm"><SelectValue placeholder="Select season" /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {seasons.map(s => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!selectedSeason && <p className="text-xs text-amber-600 mt-2">You must select a season before importing</p>}
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardHeader><CardTitle className="text-sm font-medium text-gray-700">{t('upload_file')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">{t('upload_desc')}</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={downloadTemplate} className="text-sm border-gray-300">
              <Download size={16} className="mr-2" /> {t('download_template')}
            </Button>
          </div>
          {/* Drag & Drop Zone */}
          <div
            data-testid="drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragging ? 'border-primary bg-primary/5' : file ? 'border-green-300 bg-green-50/50' : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
            }`}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input id="file-input" data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls"
              onChange={e => handleFile(e.target.files[0])} className="hidden" />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet size={24} className="text-green-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-800 text-sm">{file.name}</p>
                  <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB — Click or drop to replace</p>
                </div>
              </div>
            ) : (
              <>
                <Upload size={32} className={`mx-auto mb-3 ${dragging ? 'text-primary' : 'text-gray-300'}`} />
                <p className="text-sm font-medium text-gray-600">Drag & drop your schedule file here</p>
                <p className="text-xs text-gray-400 mt-1">or click to browse — CSV, XLSX, XLS supported</p>
              </>
            )}
          </div>
          {file && (
            <Button data-testid="upload-btn" onClick={handleUpload} disabled={uploading} className="bg-gray-900 hover:bg-gray-800 text-sm h-10 w-full">
              <Upload size={16} className="mr-2" /> {uploading ? t('parsing') : t('upload_preview')}
            </Button>
          )}
        </CardContent>
      </Card>

      {preview && (
        <Card className="bg-white border-gray-200/80 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-medium text-gray-700">{t('preview')}</CardTitle>
              <p className="text-base text-gray-500 mt-1">
                {t('total')}: {preview.total_rows} | {t('valid')}: <span className="text-green-600 font-medium">{preview.valid_rows}</span> | {t('invalid')}: <span className="text-red-600 font-medium">{preview.invalid_rows}</span>
              </p>
            </div>
            <Button data-testid="save-import-btn" onClick={handleSave} disabled={saving || preview.valid_rows === 0}
              className="bg-gray-900 hover:bg-gray-800 text-sm h-10">
              <Save size={16} className="mr-2" /> {saving ? t('saving') : t('save_matches', { count: preview.valid_rows })}
            </Button>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
                <TableHead className="text-xs text-gray-400 font-medium">#</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('valid')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('match')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('home_team')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('away_team')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('date')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('errors')}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {preview.rows.map(r => (
                  <TableRow key={r.row_number} className={`border-gray-100 ${!r.valid ? 'bg-red-50' : ''}`}>
                    <TableCell className="text-base text-gray-700">{r.row_number}</TableCell>
                    <TableCell>{r.valid ? <CheckCircle size={16} className="text-green-600" /> : <XCircle size={16} className="text-red-600" />}</TableCell>
                    <TableCell className="text-base text-gray-700">{r.match_title}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><TeamLogo name={r.home_team} size={22} /><span className="text-base text-gray-700">{r.home_team}</span></div></TableCell>
                    <TableCell><div className="flex items-center gap-2"><TeamLogo name={r.away_team} size={22} /><span className="text-base text-gray-700">{r.away_team}</span></div></TableCell>
                    <TableCell className="text-base text-gray-700">{r.match_date}</TableCell>
                    <TableCell className="text-sm text-red-600">{r.errors?.join(', ') || '---'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
